import { useMemo, useState } from "react";
import { Plus, Search, X } from "lucide-react";
import { lineTotalWithVat, fmtPrice } from "@/lib/totals";

/**
 * Vista agrupada de los items de un documento por reparación (`repair_id_ref`).
 *
 * Cada bloque renderiza:
 *  - Banda negra con "REP-code · device | model | serial" + label "Precio (IVA incluido)"
 *  - Filas: garantía (dropdown meses), nombre del concepto (con buscador de
 *    inventario), precio total IVA incl. y botón de eliminar.
 *  - Botón "Añadir concepto" al pie del bloque.
 *
 * Items sin `repair_id_ref` heredan `docRepairId` si existe; en su defecto
 * caen en el grupo virtual "Sin reparación" (`__none__`).
 *
 * Extraído de `pages/Billing.jsx` en el refactor preventivo de iteración 66.
 */
export default function GroupedItemsView({ items, setItems, parts, repairLookup, sign, docRepairId }) {
  const [openSearch, setOpenSearch] = useState(null);
  const [partQuery, setPartQuery] = useState("");

  const setItem = (i, patch) => {
    const next = items.slice();
    const merged = { ...next[i], ...patch };
    // Iter 168 — si cambia algo que afecta al precio, el line_total guardado deja
    // de ser válido: se elimina para que los totales se recalculen en vivo.
    if ("unit_price" in patch || "quantity" in patch || "discount_pct" in patch) delete merged.line_total;
    next[i] = merged;
    setItems(next);
  };
  const removeLine = (i) => setItems(items.filter((_, idx) => idx !== i));

  // Construimos grupos preservando el orden de aparición de cada repair_id_ref.
  // Items legacy sin `repair_id_ref` heredan el `repair_id` del documento (si
  // existe) para que el bloque muestre la reparación origen.
  const groups = useMemo(() => {
    const orderedKeys = [];
    const byKey = new Map();
    items.forEach((it, idx) => {
      let key = (it.repair_id_ref || "").trim();
      if (!key && docRepairId) key = docRepairId;
      if (!key) key = "__none__";
      if (!byKey.has(key)) {
        byKey.set(key, []);
        orderedKeys.push(key);
      }
      byKey.get(key).push({ it, idx });
    });
    return orderedKeys.map((k) => ({ key: k, lines: byKey.get(k) }));
  }, [items, docRepairId]);

  const filteredParts = useMemo(() => {
    if (!partQuery.trim()) return parts.slice(0, 8);
    const n = partQuery.toLowerCase();
    return parts.filter((p) =>
      (p.name || "").toLowerCase().includes(n) ||
      (p.sku || "").toLowerCase().includes(n) ||
      (p.category || "").toLowerCase().includes(n)
    ).slice(0, 10);
  }, [partQuery, parts]);

  const pickPart = (i, p) => {
    setItem(i, {
      product_id: p.id, sku: p.sku, name: p.name,
      kind: p.kind || "product",
      unit_price: p.sale_price || 0,
      vat_rate: p.vat_rate ?? 21,
      warranty_months: Number(p.warranty_months) || 0,
    });
    setOpenSearch(null);
    setPartQuery("");
  };

  const addConceptToGroup = (groupKey) => {
    const repRef = groupKey === "__none__" ? "" : groupKey;
    setItems([...items, {
      product_id: null, sku: "", name: "", description: "",
      kind: "product",
      repair_id_ref: repRef,
      quantity: 1, unit_price: 0, vat_rate: 21,
    }]);
  };

  const resolveRep = (key) => {
    if (key === "__none__") return null;
    return repairLookup[key] || (key === docRepairId ? repairLookup.__doc__ : null);
  };

  return (
    <div className="space-y-4" data-testid="docview-groups">
      {groups.length === 0 && (
        <div className="text-center text-sm text-[#525252] py-6 border border-dashed border-[#E5E5E0]">
          Sin conceptos. Añade una reparación o un concepto para empezar.
        </div>
      )}
      {groups.map((g, gIdx) => {
        const rep = resolveRep(g.key);
        const repCode = rep?.code || (g.key === "__none__" ? "" : g.key.slice(0, 8));
        const repParts = rep
          ? [rep.device_type, rep.model, rep.serial].map((p) => (p || "").trim()).filter(Boolean)
          : [];
        return (
          <div
            key={g.key}
            className="border border-[#E5E5E0] bg-white"
            data-testid={`docview-group-${gIdx}`}
          >
            {/* Iter 61: Banda negra — REP-XXX · device | model | serial   →   Precio (IVA incluido) */}
            <div className="bg-[#111110] text-white px-4 py-2 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-display font-bold text-sm" data-testid={`docview-group-code-${gIdx}`}>
                  {g.key === "__none__" ? "Sin reparación" : (repCode || "—")}
                </span>
                {repParts.length > 0 && (
                  <span className="font-mono text-[11px] text-[#E5E5E0]">
                    · {repParts.join(" | ")}
                  </span>
                )}
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-white">
                Precio (IVA incluido)
              </span>
            </div>

            {/* Conceptos del bloque (Iter 61: inline "Garantía: X meses · Concepto: Y") */}
            <table className="w-full text-xs">
              <tbody>
                {g.lines.map(({ it, idx: i }) => {
                  const qty = Number(it.quantity) || 1;
                  const unit = Number(it.unit_price) || 0;
                  const lineTotal = qty * unit;
                  const finalLine = lineTotalWithVat(qty, unit, it.discount_pct);
                  const wMonths = Number(it.warranty_months) || 0;
                  const warrantyLabel = wMonths === 0
                    ? "Sin garantía"
                    : wMonths === 1
                      ? "1 mes"
                      : `${wMonths} meses`;
                  return (
                    <tr key={i} className="border-b border-[#E5E5E0] last:border-0">
                      <td className="py-2 pl-4 pr-2 align-top w-44">
                        <div className="flex items-center gap-1 text-xs">
                          <span className="font-mono uppercase text-[10px] tracking-widest text-[#525252]">Garantía:</span>
                          <select
                            value={wMonths}
                            onChange={(e) => setItem(i, { warranty_months: parseInt(e.target.value, 10) || 0 })}
                            data-testid={`docview-warranty-${i}`}
                            className="ds-card outline-none px-1 py-0.5 text-xs rounded-none"
                          >
                            {(() => {
                              const cur = wMonths;
                              const baseOpts = [0, 3, 6, 12, 24];
                              const opts = baseOpts.includes(cur) ? baseOpts : [...baseOpts, cur].sort((a, b) => a - b);
                              return opts.map((m) => (
                                <option key={m} value={m}>
                                  {m === 0 ? "Sin garantía" : m === 1 ? "1 mes" : `${m} meses`}
                                </option>
                              ));
                            })()}
                          </select>
                        </div>
                        <div className="text-[9px] font-mono text-[#A8A8A0] mt-0.5">{warrantyLabel}</div>
                      </td>
                      <td className="py-2 pr-2 relative">
                        <div className="flex items-center gap-1">
                          <span className="font-mono uppercase text-[10px] tracking-widest text-[#525252] shrink-0">Concepto:</span>
                          <input
                            value={it.name || ""}
                            onChange={(e) => setItem(i, { name: e.target.value })}
                            placeholder="Nombre del concepto"
                            data-testid={`docview-name-${i}`}
                            className="flex-1 ds-card outline-none px-2 py-1 font-semibold rounded-none"
                          />
                          <button
                            type="button"
                            onClick={() => setOpenSearch(openSearch === i ? null : i)}
                            title="Buscar en inventario"
                            className="border border-[#E5E5E0] hover:border-[#111110] px-1.5 py-1"
                          >
                            <Search size={12} />
                          </button>
                        </div>
                        {openSearch === i && (
                          <div className="absolute z-10 left-2 right-2 mt-1 border border-[#6E8B3D] bg-white shadow-hard-sm">
                            <input
                              autoFocus value={partQuery}
                              onChange={(e) => setPartQuery(e.target.value)}
                              placeholder="Buscar producto..."
                              className="w-full border-b border-[#E5E5E0] outline-none px-2 py-1.5 text-xs"
                            />
                            <div className="max-h-44 overflow-y-auto">
                              {filteredParts.length === 0 ? (
                                <div className="p-2 text-[10px] text-[#525252]">Sin resultados</div>
                              ) : filteredParts.map((p) => (
                                <button
                                  key={p.id} type="button" onClick={() => pickPart(i, p)}
                                  className="w-full text-left px-2 py-1 hover:bg-[#F0F4E8] border-b border-[#E5E5E0] last:border-0"
                                >
                                  <div className="font-semibold text-xs">{p.name}</div>
                                  <div className="font-mono text-[10px] text-[#525252]">{p.sku} · €{p.sale_price?.toFixed(2)}</div>
                                </button>
                              ))}
                            </div>
                            <button type="button" onClick={() => setOpenSearch(null)} className="w-full text-[10px] text-[#525252] py-1">Cerrar</button>
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-4 pl-2 align-top w-36">
                        <div className="flex items-center justify-end gap-1">
                          <input
                            type="number" step="0.01"
                            value={lineTotal.toFixed(2)}
                            onChange={(e) => {
                              const newTotal = Number(e.target.value) || 0;
                              const q = Number(it.quantity) || 1;
                              setItem(i, { unit_price: q > 0 ? newTotal / q : newTotal });
                            }}
                            data-testid={`docview-price-${i}`}
                            className="w-24 ds-card outline-none px-1 py-1 font-mono text-right rounded-none"
                          />
                          <span className="text-xs">€</span>
                          {Math.abs(finalLine - lineTotal) > 0.001 && (
                            <span className="font-mono text-[10px] font-bold text-[#6E8B3D] whitespace-nowrap" data-testid={`docview-final-${i}`}>
                              Final: {fmtPrice(finalLine)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 pr-4 align-top w-6">
                        <button
                          type="button" onClick={() => removeLine(i)}
                          className="text-[#525252] hover:text-red-700"
                          data-testid={`docview-remove-${i}`}
                        >
                          <X size={12} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="px-4 py-2 border-t border-[#E5E5E0] bg-[#FAFAF7]">
              <button
                type="button"
                onClick={() => addConceptToGroup(g.key)}
                data-testid={`docview-add-concept-${gIdx}`}
                className="text-xs text-[#6E8B3D] hover:underline inline-flex items-center gap-1"
              >
                <Plus size={11} /> Añadir concepto
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
